import React, { useState, useEffect } from 'react';
import './App.css';
import SpeedWorkflow from './SpeedWorkflow';
import FrameExtractor from './FrameExtractor';

// API URL configuration - usa variabile ambiente in produzione, origin corrente come fallback
const API_URL = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.host}`;

// Main App Component
function App() {
  const [activeTab, setActiveTab] = useState('supervised'); // Default to new supervised workflow
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);

  // Carica i workflow dal backend
  const fetchWorkflows = async () => {
    setLoadingWorkflows(true);
    try {
      const response = await fetch(`${API_URL}/api/workflows`);
      if (response.ok) {
        const data = await response.json();
        setWorkflows(data.workflows || []);
      } else {
        console.error('Error fetching workflows:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching workflows:', error);
    } finally {
      setLoadingWorkflows(false);
    }
  };

  // Carica workflows all'avvio e quando si cambia tab a "workflows"
  useEffect(() => {
    if (activeTab === 'workflows') {
      fetchWorkflows();
    }
  }, [activeTab]);

  // Handler per eliminare un workflow
  const handleDeleteWorkflow = async (workflowId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo workflow? Questa azione è irreversibile.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/workflow/${workflowId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Ricarica la lista
        fetchWorkflows();
        alert('Workflow eliminato con successo');
      } else {
        alert('Errore durante l\'eliminazione del workflow');
      }
    } catch (error) {
      console.error('Error deleting workflow:', error);
      alert('Errore durante l\'eliminazione del workflow');
    }
  };

  return (
    <div className="app">
      <Header />
      <Navigation activeTab={activeTab} setActiveTab={setActiveTab} />

      <main className="main-content">
        {activeTab === 'supervised' && <SupervisedWorkflowV2 />}
        {activeTab === 'speed' && <SpeedWorkflow />}
        {activeTab === 'create' && <CreateWorkflow />}
        {activeTab === 'workflows' && !selectedWorkflowId && <WorkflowList workflows={workflows} loading={loadingWorkflows} onDelete={handleDeleteWorkflow} onRefresh={fetchWorkflows} onView={(id) => setSelectedWorkflowId(id)} />}
        {activeTab === 'workflows' && selectedWorkflowId && <WorkflowDetails workflowId={selectedWorkflowId} onBack={() => setSelectedWorkflowId(null)} onRefresh={fetchWorkflows} />}
        {activeTab === 'frameExtractor' && <FrameExtractor />}
        {activeTab === 'settings' && <Settings />}
        {activeTab === 'monitor' && <Monitor />}
      </main>
    </div>
  );
}

// Header Component
function Header() {
  return (
    <header className="header">
      <div className="header-content">
        <h1 className="logo">🎬 UGC Content Platform</h1>
        <div className="header-actions">
          <span className="user-info">Admin User</span>
          <button className="btn-icon">⚙️</button>
        </div>
      </div>
    </header>
  );
}

// Navigation Component
function Navigation({ activeTab, setActiveTab }) {
  const tabs = [
    { id: 'supervised', label: 'Workflow Supervisionato', icon: '👁️' },
    { id: 'speed', label: 'Speed Workflow', icon: '🚀' },
    { id: 'create', label: 'Workflow Automatico (Legacy)', icon: '⚡' },
    { id: 'workflows', label: 'I Miei Workflow', icon: '📁' },
    { id: 'frameExtractor', label: 'Estrai Frame', icon: '🎬' },
    { id: 'monitor', label: 'Monitor', icon: '📊' },
    { id: 'settings', label: 'Impostazioni', icon: '⚙️' }
  ];

  return (
    <nav className="navigation">
      {tabs.map(tab => (
        <button
          key={tab.id}
          className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
          onClick={() => setActiveTab(tab.id)}
        >
          <span className="tab-icon">{tab.icon}</span>
          <span className="tab-label">{tab.label}</span>
        </button>
      ))}
    </nav>
  );
}

// Default prompts
const DEFAULT_PROMPTS = {
  imageAnalysis: `Analizza questa immagine in dettaglio e descrivi:
1. Il soggetto principale e gli elementi chiave
2. I colori dominanti e l'atmosfera
3. Lo stile visivo e il mood
4. Elementi che potrebbero essere interessanti per un video UGC
5. Suggerimenti per ricreare uno stile simile

Fornisci una descrizione dettagliata che può essere usata per generare contenuti simili.`,

  imageGeneration: `Crea un'immagine in stile UGC (User Generated Content) autentico e coinvolgente basata su questa descrizione: {ANALYSIS}

Stile richiesto:
- Aspetto naturale e spontaneo
- Illuminazione realistica
- Composizione casual ma accattivante
- Qualità professionale ma non eccessivamente editata

Parametri tecnici:
- Alta risoluzione
- Colori vivaci ma naturali
- Messa a fuoco sul soggetto principale`,

  videoSystem: `Sei un esperto di contenuti video UGC per social media (TikTok, Instagram Reels, YouTube Shorts).

Il tuo compito è creare scene video coinvolgenti e autentiche che:
1. Catturano l'attenzione nei primi 3 secondi
2. Raccontano una storia chiara e semplice
3. Usano movimenti dinamici e transizioni fluide
4. Mantengono uno stile autentico e relatable
5. Sono ottimizzate per il formato verticale

Genera 4-6 scene video che formano una narrativa completa, con descrizioni dettagliate per ogni scena includendo:
- Inquadratura e movimento camera
- Azione principale
- Durata consigliata
- Elementi visivi chiave`
};

// Create Workflow Component
function CreateWorkflow() {
  const [step, setStep] = useState(1);
  const [workflowData, setWorkflowData] = useState({
    name: '',
    image: null,
    instructions: '',
    imageAnalysisPrompt: DEFAULT_PROMPTS.imageAnalysis,
    imageGenerationPrompt: DEFAULT_PROMPTS.imageGeneration,
    videoPromptSystem: DEFAULT_PROMPTS.videoSystem,
    stylePreferences: {
      tone: 'casual',
      duration: 15,
      aspectRatios: ['9:16']  // Changed to array for multi-format support
    },
    veoFramework: {
      visualStyle: 'realistic',
      cameraMovement: 'static',
      background: '',
      lightingMood: 'natural',
      audioCue: '',
      colorPalette: 'natural',
      disableAudio: false
    }
  });

  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [creditsEstimate, setCreditsEstimate] = useState(null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setWorkflowData({ ...workflowData, image: file });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        setWorkflowData({ ...workflowData, image: file });
      } else {
        alert('Per favore carica solo file immagine (JPG, PNG, GIF)');
      }
    }
  };

  const fetchCreditsEstimate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/workflows/estimate-credits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          style_preferences: workflowData.stylePreferences,
          veo_framework: workflowData.veoFramework
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCreditsEstimate(data);
      }
    } catch (error) {
      console.error('Error fetching credits estimate:', error);
    }
  };

  const handleSubmit = async () => {
    setProcessing(true);

    try {
      console.log('Creating workflow...');
      // Step 1: Create workflow
      const createResponse = await fetch('http://localhost:8000/api/workflow/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: workflowData.name,
          user_instructions: workflowData.instructions,
          image_analysis_prompt: workflowData.imageAnalysisPrompt,
          image_generation_prompt: workflowData.imageGenerationPrompt,
          video_prompt_system: workflowData.videoPromptSystem,
          style_preferences: workflowData.stylePreferences,
          veo_framework: workflowData.veoFramework
        })
      });

      if (!createResponse.ok) {
        const errorText = await createResponse.text();
        console.error('Create workflow error:', errorText);
        throw new Error('Failed to create workflow');
      }

      const createData = await createResponse.json();
      const workflowId = createData.workflow_id;
      console.log('Workflow created:', workflowId);

      // Step 2: Upload image
      console.log('Uploading image...');
      const formData = new FormData();
      formData.append('file', workflowData.image);
      formData.append('instructions', workflowData.instructions);

      const uploadResponse = await fetch(`http://localhost:8000/api/workflow/${workflowId}/upload-image`, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('Upload error:', errorText);
        throw new Error('Failed to upload image');
      }

      console.log('Image uploaded, starting to poll status...');
      // Step 3: Poll for results
      pollWorkflowStatus(workflowId);
    } catch (error) {
      console.error('Error:', error);
      alert('Errore durante la creazione del workflow: ' + error.message);
      setProcessing(false);
    }
  };

  const pollWorkflowStatus = async (workflowId) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`http://localhost:8000/api/workflow/${workflowId}/status`);
        const data = await response.json();

        if (data.status === 'completed') {
          setResult(data.results);
          setProcessing(false);
          clearInterval(interval);
        } else if (data.status === 'failed') {
          setProcessing(false);
          clearInterval(interval);
          alert('Workflow failed');
        }
      } catch (error) {
        console.error('Error polling:', error);
      }
    }, 5000);
  };

  return (
    <div className="create-workflow">
      <div className="workflow-header">
        <h2>Crea Nuovo Contenuto UGC</h2>
        <div className="steps-indicator">
          {[1, 2, 3, 4, 5].map(num => (
            <div
              key={num}
              className={`step ${step >= num ? 'active' : ''}`}
            >
              {num}
            </div>
          ))}
        </div>
      </div>

      {step === 1 && (
        <div className="step-content">
          <h3>📸 Carica Immagine di Riferimento</h3>
          <div className="upload-section">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              id="image-upload"
              style={{ display: 'none' }}
            />
            <label
              htmlFor="image-upload"
              className="upload-area"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {workflowData.image ? (
                <div className="image-preview">
                  <img
                    src={URL.createObjectURL(workflowData.image)}
                    alt="Preview"
                  />
                  <p>{workflowData.image.name}</p>
                </div>
              ) : (
                <div className="upload-placeholder">
                  <span className="upload-icon">📤</span>
                  <p>Trascina un'immagine qui o clicca per caricare</p>
                  <small>Formati supportati: JPG, PNG, GIF</small>
                </div>
              )}
            </label>
          </div>
          
          <div className="form-group">
            <label>Nome del Workflow</label>
            <input
              type="text"
              value={workflowData.name}
              onChange={(e) => setWorkflowData({ ...workflowData, name: e.target.value })}
              placeholder="Es. Campagna prodotto estate 2025"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Istruzioni per il contenuto</label>
            <textarea
              value={workflowData.instructions}
              onChange={(e) => setWorkflowData({ ...workflowData, instructions: e.target.value })}
              placeholder="Descrivi cosa vuoi ottenere..."
              className="form-textarea"
              rows="4"
            />
          </div>

          <button 
            className="btn-primary"
            onClick={() => setStep(2)}
            disabled={!workflowData.image || !workflowData.name}
          >
            Continua →
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="step-content">
          <h3>🤖 Configura Prompts AI</h3>

          <div className="prompt-section">
            <div className="prompt-header">
              <h4>Prompt Analisi Immagine</h4>
              <button
                className="btn-secondary small"
                onClick={() => setWorkflowData({
                  ...workflowData,
                  imageAnalysisPrompt: DEFAULT_PROMPTS.imageAnalysis
                })}
              >
                🔄 Ripristina Predefinito
              </button>
            </div>
            <div className="prompt-editor">
              <textarea
                value={workflowData.imageAnalysisPrompt}
                onChange={(e) => setWorkflowData({
                  ...workflowData,
                  imageAnalysisPrompt: e.target.value
                })}
                className="form-textarea code"
                rows="8"
              />
            </div>
          </div>

          <div className="prompt-section">
            <div className="prompt-header">
              <h4>Prompt Generazione Immagine</h4>
              <button
                className="btn-secondary small"
                onClick={() => setWorkflowData({
                  ...workflowData,
                  imageGenerationPrompt: DEFAULT_PROMPTS.imageGeneration
                })}
              >
                🔄 Ripristina Predefinito
              </button>
            </div>
            <div className="prompt-editor">
              <textarea
                value={workflowData.imageGenerationPrompt}
                onChange={(e) => setWorkflowData({
                  ...workflowData,
                  imageGenerationPrompt: e.target.value
                })}
                className="form-textarea code"
                rows="8"
              />
            </div>
          </div>

          <div className="button-group">
            <button className="btn-secondary" onClick={() => setStep(1)}>
              ← Indietro
            </button>
            <button className="btn-primary" onClick={() => setStep(3)}>
              Continua →
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="step-content">
          <h3>🎥 Preferenze Video</h3>

          <div className="form-group">
            <label>Tono del Contenuto</label>
            <select
              value={workflowData.stylePreferences.tone}
              onChange={(e) => setWorkflowData({
                ...workflowData,
                stylePreferences: {
                  ...workflowData.stylePreferences,
                  tone: e.target.value
                }
              })}
              className="form-select"
            >
              <option value="casual">Casual</option>
              <option value="professionale">Professionale</option>
              <option value="divertente">Divertente</option>
              <option value="emozionale">Emozionale</option>
            </select>
          </div>

          <div className="form-group">
            <label>Durata Video (secondi)</label>
            <input
              type="number"
              value={workflowData.stylePreferences.duration}
              onChange={(e) => setWorkflowData({
                ...workflowData,
                stylePreferences: {
                  ...workflowData.stylePreferences,
                  duration: parseInt(e.target.value)
                }
              })}
              min="5"
              max="60"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Formati Video (seleziona uno o più)</label>
            <div className="checkbox-group">
              {[
                { value: '9:16', label: 'Verticale (TikTok/Reels)' },
                { value: '16:9', label: 'Orizzontale (YouTube)' },
                { value: '1:1', label: 'Quadrato (Instagram)' }
              ].map(format => (
                <label key={format.value} className="checkbox-label">
                  <input
                    type="checkbox"
                    value={format.value}
                    checked={workflowData.stylePreferences.aspectRatios.includes(format.value)}
                    onChange={(e) => {
                      const ratios = workflowData.stylePreferences.aspectRatios;
                      const newRatios = e.target.checked
                        ? [...ratios, format.value]
                        : ratios.filter(r => r !== format.value);

                      setWorkflowData({
                        ...workflowData,
                        stylePreferences: {
                          ...workflowData.stylePreferences,
                          aspectRatios: newRatios
                        }
                      });
                    }}
                  />
                  <span>{format.value} - {format.label}</span>
                </label>
              ))}
            </div>
            <small className="hint">
              ⚠️ Ogni formato aggiuntivo moltiplica i crediti VEO necessari (150 crediti per video)
            </small>
          </div>

          <div className="prompt-section">
            <div className="prompt-header">
              <h4>System Prompt Video (Avanzato)</h4>
              <button
                className="btn-secondary small"
                onClick={() => setWorkflowData({
                  ...workflowData,
                  videoPromptSystem: DEFAULT_PROMPTS.videoSystem
                })}
              >
                🔄 Ripristina Predefinito
              </button>
            </div>
            <textarea
              value={workflowData.videoPromptSystem}
              onChange={(e) => setWorkflowData({
                ...workflowData,
                videoPromptSystem: e.target.value
              })}
              className="form-textarea code"
              rows="10"
            />
          </div>

          <div className="button-group">
            <button className="btn-secondary" onClick={() => setStep(2)}>
              ← Indietro
            </button>
            <button
              className="btn-primary"
              onClick={() => setStep(4)}
              disabled={workflowData.stylePreferences.aspectRatios.length === 0}
            >
              Impostazioni Avanzate →
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="step-content">
          <h3>🎨 Impostazioni Avanzate VEO</h3>
          <p className="step-description">
            Personalizza lo stile visivo dei tuoi video secondo il framework VEO 3
          </p>

          {/* Visual Style */}
          <div className="form-group">
            <label>Visual Style</label>
            <select
              value={workflowData.veoFramework.visualStyle}
              onChange={(e) => setWorkflowData({
                ...workflowData,
                veoFramework: { ...workflowData.veoFramework, visualStyle: e.target.value }
              })}
              className="form-select"
            >
              <option value="realistic">Realistic</option>
              <option value="cinematic">Cinematic</option>
              <option value="documentary">Documentary style</option>
              <option value="music-video">Music video aesthetic</option>
              <option value="wes-anderson">Wes Anderson style</option>
              <option value="animated">Animated</option>
              <option value="surreal">Surreal</option>
            </select>
          </div>

          {/* Camera Movement */}
          <div className="form-group">
            <label>Camera Movement</label>
            <select
              value={workflowData.veoFramework.cameraMovement}
              onChange={(e) => setWorkflowData({
                ...workflowData,
                veoFramework: { ...workflowData.veoFramework, cameraMovement: e.target.value }
              })}
              className="form-select"
            >
              <option value="static">Static / Locked-off</option>
              <option value="pan-left">Pan (left to right)</option>
              <option value="pan-right">Pan (right to left)</option>
              <option value="tilt-up">Tilt up</option>
              <option value="tilt-down">Tilt down</option>
              <option value="tracking">Tracking shot</option>
              <option value="dolly-in">Dolly in</option>
              <option value="dolly-out">Dolly out</option>
              <option value="aerial">Aerial / Drone</option>
              <option value="handheld">Handheld / Shaky</option>
            </select>
          </div>

          {/* Background/Setting */}
          <div className="form-group">
            <label>Background / Setting (opzionale)</label>
            <input
              type="text"
              value={workflowData.veoFramework.background}
              onChange={(e) => setWorkflowData({
                ...workflowData,
                veoFramework: { ...workflowData.veoFramework, background: e.target.value }
              })}
              placeholder="Es: Modern living room with large windows, outdoor park..."
              className="form-input"
            />
            <small className="hint">Lascia vuoto per usare il setting dall'analisi immagine</small>
          </div>

          {/* Lighting and Mood */}
          <div className="form-group">
            <label>Lighting & Mood</label>
            <select
              value={workflowData.veoFramework.lightingMood}
              onChange={(e) => setWorkflowData({
                ...workflowData,
                veoFramework: { ...workflowData.veoFramework, lightingMood: e.target.value }
              })}
              className="form-select"
            >
              <option value="natural">Natural daylight (5600K)</option>
              <option value="warm">Warm indoor (3200K)</option>
              <option value="golden-hour">Golden hour</option>
              <option value="blue-hour">Blue hour</option>
              <option value="dramatic">Dramatic / Chiaroscuro</option>
              <option value="soft">Soft / Diffused</option>
              <option value="rembrandt">Rembrandt lighting</option>
            </select>
          </div>

          {/* Color Palette */}
          <div className="form-group">
            <label>Color Palette</label>
            <select
              value={workflowData.veoFramework.colorPalette}
              onChange={(e) => setWorkflowData({
                ...workflowData,
                veoFramework: { ...workflowData.veoFramework, colorPalette: e.target.value }
              })}
              className="form-select"
            >
              <option value="natural">Natural colors</option>
              <option value="warm-tones">Warm tones</option>
              <option value="cool-tones">Cool tones</option>
              <option value="vibrant">Vibrant / Saturated</option>
              <option value="muted">Muted / Pastel</option>
              <option value="monochrome">Monochrome</option>
            </select>
          </div>

          {/* Disable Audio Toggle */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={workflowData.veoFramework.disableAudio}
                onChange={(e) => setWorkflowData({
                  ...workflowData,
                  veoFramework: {
                    ...workflowData.veoFramework,
                    disableAudio: e.target.checked,
                    audioCue: e.target.checked ? '' : workflowData.veoFramework.audioCue
                  }
                })}
              />
              <span>🔇 Disabilita audio nei video</span>
            </label>
          </div>

          {/* Audio Cue - Condizionale */}
          {!workflowData.veoFramework.disableAudio && (
            <div className="form-group">
              <label>Audio Cue (opzionale)</label>
              <input
                type="text"
                value={workflowData.veoFramework.audioCue}
                onChange={(e) => setWorkflowData({
                  ...workflowData,
                  veoFramework: { ...workflowData.veoFramework, audioCue: e.target.value }
                })}
                placeholder="Es: Upbeat background music, ambient café sounds..."
                className="form-input"
              />
            </div>
          )}

          <div className="button-group">
            <button className="btn-secondary" onClick={() => setStep(3)}>
              ← Indietro
            </button>
            <button className="btn-primary" onClick={() => {
              setStep(5);
              fetchCreditsEstimate();
            }}>
              Rivedi e Genera →
            </button>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="step-content">
          <h3>📋 Riepilogo e Conferma</h3>
          
          <div className="summary-card">
            <div className="summary-item">
              <span className="summary-label">Nome Workflow:</span>
              <span className="summary-value">{workflowData.name}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Immagine:</span>
              <span className="summary-value">{workflowData.image?.name}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Istruzioni:</span>
              <span className="summary-value">{workflowData.instructions}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Formati:</span>
              <span className="summary-value">
                {workflowData.stylePreferences.aspectRatios.join(', ')}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Stile:</span>
              <span className="summary-value">
                {workflowData.stylePreferences.tone} - {workflowData.stylePreferences.duration}s
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Visual Style:</span>
              <span className="summary-value">{workflowData.veoFramework.visualStyle}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Camera:</span>
              <span className="summary-value">{workflowData.veoFramework.cameraMovement}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Lighting:</span>
              <span className="summary-value">{workflowData.veoFramework.lightingMood}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Audio:</span>
              <span className="summary-value">
                {workflowData.veoFramework.disableAudio ? '🔇 Disabilitato' : '🔊 Abilitato'}
              </span>
            </div>

            {/* Credits Estimate */}
            {creditsEstimate && (
              <div className="credits-estimate">
                <h4>💳 Stima Crediti VEO</h4>
                <div className="credits-breakdown">
                  <p>
                    <strong>{creditsEstimate.total_credits}</strong> crediti totali
                  </p>
                  <small>{creditsEstimate.details}</small>
                  {creditsEstimate.estimated_cost_usd && (
                    <p className="cost-estimate">
                      Costo stimato: <strong>${creditsEstimate.estimated_cost_usd}</strong>
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {!processing && !result && (
            <div className="button-group">
              <button className="btn-secondary" onClick={() => setStep(4)}>
                ← Modifica Impostazioni
              </button>
              <button className="btn-primary large" onClick={handleSubmit}>
                🚀 Genera Contenuto
              </button>
            </div>
          )}

          {processing && <ProcessingStatus />}
          {result && <ResultDisplay result={result} />}
        </div>
      )}
    </div>
  );
}

// Processing Status Component
function ProcessingStatus() {
  const [status, setStatus] = useState('Inizializzazione...');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const steps = [
      { text: 'Analisi immagine...', progress: 20 },
      { text: 'Generazione prompt...', progress: 40 },
      { text: 'Creazione immagine UGC...', progress: 60 },
      { text: 'Generazione scene video...', progress: 80 },
      { text: 'Combinazione video...', progress: 95 },
      { text: 'Finalizzazione...', progress: 100 }
    ];

    let currentStep = 0;
    const interval = setInterval(() => {
      if (currentStep < steps.length) {
        setStatus(steps[currentStep].text);
        setProgress(steps[currentStep].progress);
        currentStep++;
      }
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="processing-status">
      <div className="processing-animation">
        <div className="spinner"></div>
      </div>
      <h4>{status}</h4>
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
      </div>
      <p className="processing-info">
        Questo processo può richiedere 2-3 minuti. Non chiudere questa finestra.
      </p>
    </div>
  );
}

// Result Display Component
function ResultDisplay({ result }) {
  return (
    <div className="result-display">
      <h3>✅ Contenuto Generato con Successo!</h3>
      
      <div className="result-grid">
        <div className="result-item">
          <h4>Immagine Generata</h4>
          <img src={result.generated_image} alt="Generated" className="result-image" />
          <button className="btn-secondary">Scarica Immagine</button>
        </div>
        
        <div className="result-item">
          <h4>Video Finale</h4>
          <video controls className="result-video">
            <source src={result.final_video} type="video/mp4" />
          </video>
          <button className="btn-secondary">Scarica Video</button>
        </div>
      </div>

      <div className="result-actions">
        <button className="btn-primary">Condividi su Social</button>
        <button className="btn-secondary">Salva nel Portfolio</button>
        <button className="btn-outline">Crea Nuovo Contenuto</button>
      </div>
    </div>
  );
}

// Workflow List Component
function WorkflowList({ workflows, loading, onDelete, onRefresh, onView }) {
  // Helper function to handle URLs (alcuni sono già completi, altri sono percorsi relativi)
  const getFullUrl = (url) => {
    if (!url) return '';
    // Se l'URL inizia con http:// o https://, è già completo
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Altrimenti aggiungi API_URL
    return `${API_URL}/${url}`;
  };

  const handleDownloadVideo = (workflowId) => {
    window.open(`${API_URL}/api/workflow/${workflowId}/download/video`, '_blank');
  };

  const handleDownloadImage = (workflowId) => {
    window.open(`${API_URL}/api/workflow/${workflowId}/download/image`, '_blank');
  };

  const handleDownloadAll = (workflowId) => {
    window.open(`${API_URL}/api/workflow/${workflowId}/download/all`, '_blank');
  };

  const handleRegenerate = async (workflowId) => {
    if (!window.confirm('Vuoi rigenerare questo workflow? Verrà creato un nuovo workflow basato su questo.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/workflow/${workflowId}/regenerate`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Nuovo workflow creato: ${data.name}\nID: ${data.new_workflow_id}`);
        onRefresh(); // Ricarica la lista per mostrare il nuovo workflow
      } else {
        const errorData = await response.json();
        alert(`Errore durante la rigenerazione: ${errorData.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Error regenerating workflow:', error);
      alert('Errore durante la rigenerazione del workflow');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'In Attesa',
      'processing': 'In Elaborazione',
      'completed': 'Completato',
      'failed': 'Fallito'
    };
    return labels[status] || status;
  };

  return (
    <div className="workflow-list">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>I Miei Workflow</h2>
        <button className="btn-secondary" onClick={onRefresh} disabled={loading}>
          {loading ? 'Caricamento...' : '🔄 Aggiorna'}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner"></div>
          <p>Caricamento workflow...</p>
        </div>
      ) : workflows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p style={{ fontSize: '48px' }}>📭</p>
          <p>Nessun workflow trovato</p>
          <p>Crea il tuo primo workflow per iniziare!</p>
        </div>
      ) : (
        <div className="workflow-grid">
          {workflows.map(workflow => (
            <div key={workflow.id} className="workflow-card">
              <div className="workflow-thumbnail">
                {workflow.thumbnail ? (
                  <img src={getFullUrl(workflow.thumbnail)} alt={workflow.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div className="thumbnail-placeholder">📁</div>
                )}
              </div>
              <div className="workflow-info">
                <h3>{workflow.name}</h3>
                <p className="workflow-date">
                  {formatDate(workflow.created_at)}
                </p>
                <p className="workflow-status">
                  <span className={`status-badge ${workflow.status}`}>
                    {getStatusLabel(workflow.status)}
                  </span>
                </p>
                {workflow.current_step && workflow.current_step !== 'completed' && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Step: {workflow.current_step}
                  </p>
                )}
              </div>
              <div className="workflow-actions">
                <button
                  className="btn-icon"
                  onClick={() => onView(workflow.id)}
                  title="Visualizza dettagli"
                >
                  👁️
                </button>
                {workflow.final_video_url && (
                  <button
                    className="btn-icon"
                    onClick={() => handleDownloadVideo(workflow.id)}
                    title="Scarica video"
                  >
                    📥
                  </button>
                )}
                {workflow.status === 'completed' && (
                  <button
                    className="btn-icon"
                    onClick={() => handleDownloadAll(workflow.id)}
                    title="Scarica tutto (ZIP)"
                  >
                    📦
                  </button>
                )}
                <button
                  className="btn-icon"
                  onClick={() => onDelete(workflow.id)}
                  title="Elimina workflow"
                  style={{ color: '#f44336' }}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Workflow Details Component - Vista dedicata per dettagli workflow
function WorkflowDetails({ workflowId, onBack, onRefresh }) {
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to handle URLs
  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${API_URL}/${url}`;
  };

  // Carica dettagli workflow
  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/api/workflow/${workflowId}/details`);
        if (response.ok) {
          const data = await response.json();
          setWorkflow(data);
        } else {
          setError('Errore nel caricamento dei dettagli');
        }
      } catch (err) {
        console.error('Error loading workflow details:', err);
        setError('Errore di connessione');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [workflowId]);

  const handleDownloadVideo = () => {
    window.open(`${API_URL}/api/workflow/${workflowId}/download/video`, '_blank');
  };

  const handleDownloadImage = () => {
    window.open(`${API_URL}/api/workflow/${workflowId}/download/image`, '_blank');
  };

  const handleDownloadAll = () => {
    window.open(`${API_URL}/api/workflow/${workflowId}/download/all`, '_blank');
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Vuoi rigenerare questo workflow? Verrà creato un nuovo workflow basato su questo.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/workflow/${workflowId}/regenerate`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Nuovo workflow creato: ${data.name}\nID: ${data.new_workflow_id}`);
        onRefresh();
        onBack(); // Torna alla lista
      } else {
        const errorData = await response.json();
        alert(`Errore durante la rigenerazione: ${errorData.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Error regenerating workflow:', error);
      alert('Errore durante la rigenerazione del workflow');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'In Attesa',
      'processing': 'In Elaborazione',
      'completed': 'Completato',
      'failed': 'Fallito'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto' }}></div>
        <p>Caricamento dettagli workflow...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: '#f44336', marginBottom: '20px' }}>❌ {error}</p>
        <button className="btn-secondary" onClick={onBack}>
          ← Torna alla Lista
        </button>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Workflow non trovato</p>
        <button className="btn-secondary" onClick={onBack}>
          ← Torna alla Lista
        </button>
      </div>
    );
  }

  return (
    <div className="workflow-details" style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <button className="btn-secondary" onClick={onBack}>
          ← Torna alla Lista
        </button>
        <h2 style={{ margin: 0 }}>{workflow.name}</h2>
        <div style={{ width: '150px' }}></div> {/* Spacer per centrare il titolo */}
      </div>

      {/* Info Workflow */}
      <div style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '30px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div>
            <strong>Status:</strong>
            <span className={`status-badge ${workflow.status}`} style={{ marginLeft: '10px' }}>
              {getStatusLabel(workflow.status)}
            </span>
          </div>
          <div>
            <strong>Step Corrente:</strong> {workflow.current_step}
          </div>
          <div>
            <strong>Creato:</strong> {formatDate(workflow.created_at)}
          </div>
          <div>
            <strong>Aggiornato:</strong> {formatDate(workflow.updated_at)}
          </div>
        </div>
      </div>

      {/* Istruzioni */}
      {workflow.user_instructions && (
        <div style={{ marginBottom: '30px' }}>
          <h3>📝 Istruzioni</h3>
          <div style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: '15px', borderRadius: '8px' }}>
            {workflow.user_instructions}
          </div>
        </div>
      )}

      {/* Immagine Generata */}
      {workflow.generated_image_url && (
        <div style={{ marginBottom: '30px' }}>
          <h3>🖼️ Immagine Generata</h3>
          <img
            src={getFullUrl(workflow.generated_image_url)}
            alt="Generated"
            style={{ maxWidth: '100%', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
          />
          <button
            className="btn-secondary"
            onClick={handleDownloadImage}
            style={{ marginTop: '15px' }}
          >
            📥 Scarica Immagine
          </button>
        </div>
      )}

      {/* Video Finale */}
      {workflow.final_video_url && (
        <div style={{ marginBottom: '30px' }}>
          <h3>🎬 Video Finale</h3>
          <video
            src={getFullUrl(workflow.final_video_url)}
            controls
            style={{ maxWidth: '100%', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
          />
          <div style={{ marginTop: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className="btn-primary"
              onClick={handleDownloadVideo}
            >
              📥 Scarica Video
            </button>
            <button
              className="btn-secondary"
              onClick={handleDownloadAll}
            >
              📦 Scarica Tutto (ZIP)
            </button>
            <button
              className="btn-secondary"
              onClick={handleRegenerate}
            >
              🔄 Rigenera Workflow
            </button>
          </div>
        </div>
      )}

      {/* Video Scene */}
      {workflow.video_urls && workflow.video_urls.length > 0 && (
        <div style={{ marginBottom: '30px' }}>
          <h3>🎥 Video Scene</h3>
          <div style={{ display: 'grid', gap: '20px' }}>
            {workflow.video_urls.map((url, idx) => (
              <div key={idx} style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px' }}>
                <h4 style={{ marginTop: 0 }}>Scena {idx + 1}</h4>
                <video
                  src={getFullUrl(url)}
                  controls
                  style={{ maxWidth: '100%', borderRadius: '8px' }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Settings Component
function Settings() {
  const [settings, setSettings] = useState({
    openaiKey: '',
    anthropicKey: '',
    falApiKey: '',
    veoApiKey: '',
    defaultStyle: 'casual',
    autoProcess: false,
    webhookUrl: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Carica le impostazioni all'avvio
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings({
          openaiKey: data.openai_key || '',
          anthropicKey: data.anthropic_key || '',
          falApiKey: data.fal_api_key || '',
          veoApiKey: data.veo_api_key || '',
          defaultStyle: data.default_style || 'casual',
          autoProcess: data.auto_process || false,
          webhookUrl: data.webhook_url || ''
        });
      } else {
        console.error('Error loading settings:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          openai_key: settings.openaiKey,
          anthropic_key: settings.anthropicKey,
          fal_api_key: settings.falApiKey,
          veo_api_key: settings.veoApiKey,
          default_style: settings.defaultStyle,
          auto_process: settings.autoProcess,
          webhook_url: settings.webhookUrl
        })
      });

      if (response.ok) {
        alert('Impostazioni salvate con successo!');
      } else {
        const errorData = await response.json();
        alert(`Errore nel salvatagg io: ${errorData.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Errore durante il salvataggio delle impostazioni');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings">
      <h2>Impostazioni</h2>
      
      <div className="settings-section">
        <h3>🔑 API Keys</h3>
        
        <div className="form-group">
          <label>OpenAI API Key</label>
          <input
            type="password"
            value={settings.openaiKey}
            onChange={(e) => setSettings({ ...settings, openaiKey: e.target.value })}
            className="form-input"
            placeholder="sk-..."
          />
        </div>

        <div className="form-group">
          <label>Anthropic API Key</label>
          <input
            type="password"
            value={settings.anthropicKey}
            onChange={(e) => setSettings({ ...settings, anthropicKey: e.target.value })}
            className="form-input"
            placeholder="sk-ant-..."
          />
        </div>

        <div className="form-group">
          <label>FAL.ai API Key</label>
          <input
            type="password"
            value={settings.falApiKey}
            onChange={(e) => setSettings({ ...settings, falApiKey: e.target.value })}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>VEO API Key</label>
          <input
            type="password"
            value={settings.veoApiKey}
            onChange={(e) => setSettings({ ...settings, veoApiKey: e.target.value })}
            className="form-input"
          />
        </div>
      </div>

      <div className="settings-section">
        <h3>⚙️ Configurazione Predefinita</h3>
        
        <div className="form-group">
          <label>Stile Predefinito</label>
          <select
            value={settings.defaultStyle}
            onChange={(e) => setSettings({ ...settings, defaultStyle: e.target.value })}
            className="form-select"
          >
            <option value="casual">Casual</option>
            <option value="professionale">Professionale</option>
            <option value="divertente">Divertente</option>
          </select>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.autoProcess}
              onChange={(e) => setSettings({ ...settings, autoProcess: e.target.checked })}
            />
            <span>Elaborazione automatica al caricamento</span>
          </label>
        </div>

        <div className="form-group">
          <label>Webhook URL (opzionale)</label>
          <input
            type="url"
            value={settings.webhookUrl}
            onChange={(e) => setSettings({ ...settings, webhookUrl: e.target.value })}
            className="form-input"
            placeholder="https://..."
          />
        </div>
      </div>

      <button className="btn-primary" onClick={handleSave} disabled={saving || loading}>
        {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
      </button>
    </div>
  );
}

// Monitor Component
function Monitor() {
  const [stats, setStats] = useState({
    totalWorkflows: 42,
    completedToday: 8,
    averageTime: '2m 34s',
    successRate: 94.5
  });

  return (
    <div className="monitor">
      <h2>Monitor Dashboard</h2>
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-value">{stats.totalWorkflows}</div>
          <div className="stat-label">Workflow Totali</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-value">{stats.completedToday}</div>
          <div className="stat-label">Completati Oggi</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">⏱️</div>
          <div className="stat-value">{stats.averageTime}</div>
          <div className="stat-label">Tempo Medio</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-value">{stats.successRate}%</div>
          <div className="stat-label">Tasso di Successo</div>
        </div>
      </div>

      <div className="recent-activities">
        <h3>Attività Recenti</h3>
        <div className="activity-list">
          <div className="activity-item">
            <span className="activity-time">10:23</span>
            <span className="activity-text">Workflow "Summer Campaign" completato</span>
            <span className="activity-status success">✓</span>
          </div>
          <div className="activity-item">
            <span className="activity-time">10:15</span>
            <span className="activity-text">Nuovo workflow "Product Launch" avviato</span>
            <span className="activity-status processing">⟳</span>
          </div>
          <div className="activity-item">
            <span className="activity-time">09:58</span>
            <span className="activity-text">Workflow "Test Demo" fallito - Riprova</span>
            <span className="activity-status error">✗</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SUPERVISED WORKFLOW V2 - Step by Step con REST API
// ============================================================================

function SupervisedWorkflowV2() {
  const [state, setState] = useState({
    workflow_id: null,
    current_step: 'setup',
    name: '',
    instructions: '',
    image: null,
    imagePreview: null,

    outputs: {},
    status: {},
    regeneration_count: {},
    progress: {},  // Progress tracking for long tasks

    editing: {},
    tempValues: {},

    loading: false,
    error: null
  });

  // Polling per status quando necessario
  useEffect(() => {
    if (!state.workflow_id) return;

    const processingSteps = [
      'analyzing',
      'generating_image_prompt',
      'generating_image',
      'regenerating_image',
      'generating_video_scenes',
      'generating_videos',
      'combining_videos'
    ];
    if (!processingSteps.includes(state.current_step)) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/workflow/${state.workflow_id}/status`);
        const data = await res.json();

        setState(prev => ({
          ...prev,
          current_step: data.current_step,
          outputs: data.step_outputs || {},
          status: data.step_status || {},
          regeneration_count: data.regeneration_count || {},
          progress: data.progress || {}  // Update progress
        }));
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [state.workflow_id, state.current_step]);

  // Handlers
  const handleCreateWorkflow = async () => {
    if (!state.name || !state.instructions) {
      alert('Inserisci nome e istruzioni');
      return;
    }

    setState(prev => ({ ...prev, loading: true }));

    try {
      const res = await fetch(`${API_URL}/api/workflow/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: state.name,
          user_instructions: state.instructions
        })
      });

      const data = await res.json();
      setState(prev => ({
        ...prev,
        workflow_id: data.workflow_id,
        current_step: 'upload',
        loading: false
      }));
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleUploadImage = async () => {
    if (!state.image) return;

    setState(prev => ({ ...prev, loading: true }));

    const formData = new FormData();
    formData.append('file', state.image);

    try {
      const res = await fetch(`${API_URL}/api/workflow/${state.workflow_id}/upload-image`, {
        method: 'POST',
        body: formData
      });

      const data = await res.json();
      setState(prev => ({
        ...prev,
        current_step: 'uploaded',
        outputs: { ...prev.outputs, image_url: data.image_url },
        loading: false
      }));
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleAnalyze = async () => {
    setState(prev => ({ ...prev, loading: true, current_step: 'analyzing' }));

    try {
      await fetch(`${API_URL}/api/workflow/${state.workflow_id}/analyze`, { method: 'POST' });
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleApproveAnalysis = async () => {
    setState(prev => ({ ...prev, loading: true }));

    const body = state.editing.analyze ? { edited_value: state.tempValues.analyze } : {};

    try {
      const res = await fetch(`${API_URL}/api/workflow/${state.workflow_id}/approve-analysis`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();

      setState(prev => ({
        ...prev,
        current_step: data.current_step,
        loading: false,
        editing: { ...prev.editing, analyze: false }
      }));
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleGenerateImagePrompt = async () => {
    setState(prev => ({ ...prev, loading: true, current_step: 'generating_image_prompt' }));

    try {
      await fetch(`${API_URL}/api/workflow/${state.workflow_id}/generate-image-prompt`, { method: 'POST' });
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleApproveImagePrompt = async () => {
    setState(prev => ({ ...prev, loading: true }));

    const body = state.editing.image_prompt ? { edited_value: state.tempValues.image_prompt } : {};

    try {
      const res = await fetch(`${API_URL}/api/workflow/${state.workflow_id}/approve-image-prompt`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();

      setState(prev => ({
        ...prev,
        current_step: data.current_step,
        loading: false,
        editing: { ...prev.editing, image_prompt: false }
      }));
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleGenerateImage = async () => {
    setState(prev => ({ ...prev, loading: true, current_step: 'generating_image' }));

    try {
      await fetch(`${API_URL}/api/workflow/${state.workflow_id}/generate-image`, { method: 'POST' });
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleRegenerateImage = async () => {
    setState(prev => ({ ...prev, loading: true, current_step: 'regenerating_image' }));

    try {
      await fetch(`${API_URL}/api/workflow/${state.workflow_id}/regenerate-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleApproveImage = async () => {
    setState(prev => ({ ...prev, loading: true }));

    try {
      const res = await fetch(`${API_URL}/api/workflow/${state.workflow_id}/approve-image`, { method: 'PUT' });
      const data = await res.json();

      setState(prev => ({
        ...prev,
        current_step: data.current_step,
        loading: false
      }));
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleGenerateVideoScenes = async () => {
    setState(prev => ({ ...prev, loading: true }));

    try {
      const res = await fetch(`${API_URL}/api/workflow/${state.workflow_id}/generate-video-scenes`, {
        method: 'POST'
      });
      const data = await res.json();

      setState(prev => ({
        ...prev,
        current_step: data.current_step || 'generating_video_scenes',
        loading: false
      }));
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleApproveVideoScenes = async () => {
    setState(prev => ({ ...prev, loading: true }));

    const body = state.editing.video_scenes ? { edited_value: state.tempValues.video_scenes } : {};

    try {
      const res = await fetch(`${API_URL}/api/workflow/${state.workflow_id}/approve-video-scenes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();

      setState(prev => ({
        ...prev,
        current_step: data.current_step,
        loading: false,
        editing: { ...prev.editing, video_scenes: false }
      }));
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleGenerateVideos = async () => {
    setState(prev => ({ ...prev, loading: true }));

    try {
      const res = await fetch(`${API_URL}/api/workflow/${state.workflow_id}/generate-videos`, {
        method: 'POST'
      });
      const data = await res.json();

      setState(prev => ({
        ...prev,
        current_step: data.current_step || 'generating_videos',
        loading: false
      }));
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleApproveVideos = async () => {
    setState(prev => ({ ...prev, loading: true }));

    try {
      const res = await fetch(`${API_URL}/api/workflow/${state.workflow_id}/approve-videos`, {
        method: 'PUT'
      });
      const data = await res.json();

      setState(prev => ({
        ...prev,
        current_step: data.current_step,
        loading: false
      }));
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleCombineVideos = async () => {
    setState(prev => ({ ...prev, loading: true }));

    try {
      const res = await fetch(`${API_URL}/api/workflow/${state.workflow_id}/combine-videos`, {
        method: 'POST'
      });
      const data = await res.json();

      setState(prev => ({
        ...prev,
        current_step: data.current_step || 'combining_videos',
        loading: false
      }));
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleCancelWorkflow = async () => {
    if (!window.confirm('Sei sicuro di voler cancellare questo workflow? Tornerà allo step precedente.')) {
      return;
    }

    setState(prev => ({ ...prev, loading: true }));

    try {
      const res = await fetch(`${API_URL}/api/workflow/${state.workflow_id}/cancel`, {
        method: 'DELETE'
      });
      const data = await res.json();

      setState(prev => ({
        ...prev,
        current_step: data.current_step,
        status: data.step_status,
        loading: false
      }));

      alert('Workflow cancellato. Puoi riprovare lo step.');
    } catch (err) {
      setState(prev => ({ ...prev, error: err.message, loading: false }));
      alert('Errore nella cancellazione: ' + err.message);
    }
  };

  return (
    <div className="supervised-workflow-v2" style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
      <h2>🎬 Workflow Supervisionato Step-by-Step</h2>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Ogni output AI richiede approvazione prima di proseguire
      </p>

      {state.error && (
        <div style={{ background: '#fee', padding: '15px', borderRadius: '8px', marginBottom: '20px', color: '#c00' }}>
          ❌ {state.error}
          <button onClick={() => setState(prev => ({ ...prev, error: null }))} style={{ float: 'right' }}>✕</button>
        </div>
      )}

      {/* Step Indicator */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
        <StepBadge label="Setup" active={state.current_step === 'setup'} completed={state.workflow_id !== null} />
        <StepBadge label="Upload" active={state.current_step === 'upload'} completed={state.current_step !== 'setup' && state.current_step !== 'upload'} />
        <StepBadge label="Analyze" active={state.current_step.includes('analy')} completed={state.status.analyze === 'approved'} />
        <StepBadge label="Prompt" active={state.current_step.includes('prompt')} completed={state.status.image_prompt === 'approved'} />
        <StepBadge label="Image" active={state.current_step.includes('image_')} completed={state.status.image_gen === 'approved'} />
      </div>

      {/* STEP: Setup */}
      {state.current_step === 'setup' && (
        <div className="step-content">
          <h3>📝 Configura Workflow</h3>

          <div className="form-group">
            <label>Nome Workflow</label>
            <input
              type="text"
              value={state.name}
              onChange={e => setState(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Es: Summer Campaign 2025"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Istruzioni</label>
            <textarea
              value={state.instructions}
              onChange={e => setState(prev => ({ ...prev, instructions: e.target.value }))}
              placeholder="Cosa vuoi ottenere..."
              className="form-textarea"
              rows="4"
            />
          </div>

          <button
            className="btn-primary"
            onClick={handleCreateWorkflow}
            disabled={state.loading}
          >
            {state.loading ? 'Creazione...' : 'Crea Workflow →'}
          </button>
        </div>
      )}

      {/* STEP: Upload */}
      {state.current_step === 'upload' && (
        <div className="step-content">
          <h3>📤 Upload Immagine</h3>

          <input
            type="file"
            accept="image/*"
            onChange={e => {
              const file = e.target.files[0];
              if (file) {
                setState(prev => ({
                  ...prev,
                  image: file,
                  imagePreview: URL.createObjectURL(file)
                }));
              }
            }}
            id="img-upload"
            style={{ display: 'none' }}
          />

          <label htmlFor="img-upload" className="upload-area" style={{ cursor: 'pointer', display: 'block', padding: '40px', border: '2px dashed #ccc', borderRadius: '8px', textAlign: 'center' }}>
            {state.imagePreview ? (
              <img src={state.imagePreview} alt="Preview" style={{ maxWidth: '400px' }} />
            ) : (
              <div>
                <span style={{ fontSize: '48px' }}>📤</span>
                <p>Clicca per caricare</p>
              </div>
            )}
          </label>

          <button
            className="btn-primary"
            onClick={handleUploadImage}
            disabled={!state.image || state.loading}
            style={{ marginTop: '20px' }}
          >
            {state.loading ? 'Caricamento...' : 'Carica →'}
          </button>
        </div>
      )}

      {/* STEP: Start Analysis */}
      {state.current_step === 'uploaded' && (
        <div className="step-content">
          <h3>🔍 Step 2: Analisi Immagine</h3>
          <p>Avvia analisi con OpenAI Vision</p>

          <button className="btn-primary" onClick={handleAnalyze}>
            🚀 Avvia Analisi
          </button>
        </div>
      )}

      {/* STEP: Analyzing */}
      {state.current_step === 'analyzing' && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
          <p>Analisi in corso...</p>
          <button
            className="btn-secondary"
            onClick={handleCancelWorkflow}
            style={{ marginTop: '20px' }}
          >
            ❌ Cancella Workflow
          </button>
        </div>
      )}

      {/* STEP: Review Analysis */}
      {state.current_step === 'analysis_ready' && (
        <div className="step-content">
          <h3>✅ Analisi Completata</h3>

          <div style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <h4>Descrizione Immagine:</h4>
            {!state.editing.analyze ? (
              <>
                <p style={{ whiteSpace: 'pre-wrap' }}>{state.outputs.analyze}</p>
                <div style={{ marginTop: '15px' }}>
                  <button
                    className="btn-secondary"
                    onClick={() => setState(prev => ({
                      ...prev,
                      editing: { ...prev.editing, analyze: true },
                      tempValues: { ...prev.tempValues, analyze: prev.outputs.analyze }
                    }))}
                  >
                    ✏️ Modifica
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleApproveAnalysis}
                    style={{ marginLeft: '10px' }}
                  >
                    ✅ Approva
                  </button>
                </div>
              </>
            ) : (
              <>
                <textarea
                  value={state.tempValues.analyze}
                  onChange={e => setState(prev => ({
                    ...prev,
                    tempValues: { ...prev.tempValues, analyze: e.target.value }
                  }))}
                  className="form-textarea"
                  rows="8"
                />
                <div style={{ marginTop: '15px' }}>
                  <button
                    className="btn-secondary"
                    onClick={() => setState(prev => ({ ...prev, editing: { ...prev.editing, analyze: false } }))}
                  >
                    ❌ Annulla
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleApproveAnalysis}
                    style={{ marginLeft: '10px' }}
                  >
                    💾 Salva e Approva
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* STEP: Generate Image Prompt */}
      {state.current_step === 'analysis_approved' && (
        <div className="step-content">
          <h3>✨ Step 3: Genera Image Prompt</h3>
          <p>Claude genererà un prompt ottimizzato</p>

          <button className="btn-primary" onClick={handleGenerateImagePrompt}>
            🚀 Genera Prompt
          </button>
        </div>
      )}

      {state.current_step === 'generating_image_prompt' && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
          <p>Generazione prompt...</p>
          <button
            className="btn-secondary"
            onClick={handleCancelWorkflow}
            style={{ marginTop: '20px' }}
          >
            ❌ Cancella Workflow
          </button>
        </div>
      )}

      {/* STEP: Review Image Prompt */}
      {state.current_step === 'image_prompt_ready' && (
        <div className="step-content">
          <h3>✅ Prompt Generato</h3>

          <div style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <h4>Image Prompt:</h4>
            {!state.editing.image_prompt ? (
              <>
                <p style={{ whiteSpace: 'pre-wrap' }}>{state.outputs.image_prompt}</p>
                <div style={{ marginTop: '15px' }}>
                  <button
                    className="btn-secondary"
                    onClick={() => setState(prev => ({
                      ...prev,
                      editing: { ...prev.editing, image_prompt: true },
                      tempValues: { ...prev.tempValues, image_prompt: prev.outputs.image_prompt }
                    }))}
                  >
                    ✏️ Modifica
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleApproveImagePrompt}
                    style={{ marginLeft: '10px' }}
                  >
                    ✅ Approva
                  </button>
                </div>
              </>
            ) : (
              <>
                <textarea
                  value={state.tempValues.image_prompt}
                  onChange={e => setState(prev => ({
                    ...prev,
                    tempValues: { ...prev.tempValues, image_prompt: e.target.value }
                  }))}
                  className="form-textarea"
                  rows="8"
                />
                <div style={{ marginTop: '15px' }}>
                  <button
                    className="btn-secondary"
                    onClick={() => setState(prev => ({ ...prev, editing: { ...prev.editing, image_prompt: false } }))}
                  >
                    ❌ Annulla
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleApproveImagePrompt}
                    style={{ marginLeft: '10px' }}
                  >
                    💾 Salva e Approva
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* STEP: Generate Image */}
      {state.current_step === 'image_prompt_approved' && (
        <div className="step-content">
          <h3>🎨 Step 4: Genera Immagine</h3>
          <p>FAL.ai genererà l'immagine UGC</p>

          <button className="btn-primary" onClick={handleGenerateImage}>
            🚀 Genera Immagine
          </button>
        </div>
      )}

      {(state.current_step === 'generating_image' || state.current_step === 'regenerating_image') && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
          <p>Generazione immagine...</p>

          {state.progress.image_gen && (
            <ProgressBar progress={state.progress.image_gen} />
          )}

          <button
            className="btn-secondary"
            onClick={handleCancelWorkflow}
            style={{ marginTop: '20px' }}
          >
            ❌ Cancella Workflow
          </button>
        </div>
      )}

      {/* STEP: Review Image */}
      {state.current_step === 'image_ready' && (
        <div className="step-content">
          <h3>✅ Immagine Generata</h3>

          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <img
              src={state.outputs.generated_image}
              alt="Generated"
              style={{ maxWidth: '100%', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              className="btn-secondary"
              onClick={handleRegenerateImage}
              disabled={(state.regeneration_count.image_gen || 0) >= 3}
            >
              🔄 Rigenera {state.regeneration_count.image_gen ? `(${state.regeneration_count.image_gen}/3)` : ''}
            </button>
            <button
              className="btn-primary"
              onClick={handleApproveImage}
            >
              ✅ Approva Immagine
            </button>
          </div>
        </div>
      )}

      {/* STEP 5: VIDEO SCENES GENERATION */}
      {state.current_step === 'image_approved' && (
        <div className="step-content">
          <h3>🎬 Step 5: Generazione Scene Video</h3>
          <p>L'immagine è stata approvata. Procedi alla generazione delle scene video.</p>
          <button
            className="btn-primary"
            onClick={handleGenerateVideoScenes}
            disabled={state.loading}
          >
            📹 Genera Scene Video
          </button>
        </div>
      )}

      {state.current_step === 'generating_video_scenes' && (
        <div className="step-content">
          <h3>⏳ Generazione Scene in Corso...</h3>
          <div className="loader"></div>
          <p>Claude sta creando le descrizioni per 3 scene video...</p>
          <button
            className="btn-secondary"
            onClick={handleCancelWorkflow}
            style={{ marginTop: '20px' }}
          >
            ❌ Cancella Workflow
          </button>
        </div>
      )}

      {state.current_step === 'video_scenes_ready' && (
        <div className="step-content">
          <h3>✅ Scene Video Generate</h3>

          {state.outputs.video_scenes && state.outputs.video_scenes.scenes && (
            <div style={{ background: '#f5f5f5', padding: '20px', borderRadius: '8px', marginBottom: '20px', textAlign: 'left' }}>
              {Object.entries(state.outputs.video_scenes.scenes).map(([key, value]) => (
                <div key={key} style={{ marginBottom: '15px' }}>
                  <strong style={{ textTransform: 'capitalize' }}>{key.replace('_', ' ')}:</strong>
                  <p style={{ marginLeft: '10px', color: '#555' }}>{value}</p>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            {state.editing.video_scenes && (
              <div style={{ width: '100%', marginBottom: '15px' }}>
                <textarea
                  value={state.tempValues.video_scenes || JSON.stringify(state.outputs.video_scenes, null, 2)}
                  onChange={(e) => setState(prev => ({
                    ...prev,
                    tempValues: { ...prev.tempValues, video_scenes: e.target.value }
                  }))}
                  style={{ width: '100%', minHeight: '200px', padding: '10px', fontFamily: 'monospace' }}
                />
              </div>
            )}

            <button
              className="btn-secondary"
              onClick={() => setState(prev => ({
                ...prev,
                editing: { ...prev.editing, video_scenes: !prev.editing.video_scenes }
              }))}
            >
              ✏️ {state.editing.video_scenes ? 'Annulla' : 'Modifica'}
            </button>
            <button
              className="btn-primary"
              onClick={handleApproveVideoScenes}
            >
              ✅ Approva Scene
            </button>
          </div>
        </div>
      )}

      {/* STEP 6: VIDEO GENERATION */}
      {state.current_step === 'video_scenes_approved' && (
        <div className="step-content">
          <h3>🎥 Step 6: Generazione Video</h3>
          <p>Scene approvate. Procedi alla generazione dei video.</p>
          <button
            className="btn-primary"
            onClick={handleGenerateVideos}
            disabled={state.loading}
          >
            🎬 Genera Video (VEO)
          </button>
        </div>
      )}

      {state.current_step === 'generating_videos' && (
        <div className="step-content">
          <h3>⏳ Generazione Video in Corso...</h3>
          <div className="loader"></div>
          <p>VEO sta creando i video per ogni scena (questo può richiedere alcuni minuti)...</p>
          <button
            className="btn-secondary"
            onClick={handleCancelWorkflow}
            style={{ marginTop: '20px' }}
          >
            ❌ Cancella Workflow
          </button>
        </div>
      )}

      {state.current_step === 'videos_ready' && (
        <div className="step-content">
          <h3>✅ Video Generati</h3>

          {state.outputs.video_urls && state.outputs.video_urls.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              {state.outputs.video_urls.map((url, idx) => (
                <div key={idx} style={{ marginBottom: '15px' }}>
                  <p><strong>Scena {idx + 1}:</strong></p>
                  <video
                    src={url}
                    controls
                    style={{ maxWidth: '100%', borderRadius: '8px' }}
                  />
                </div>
              ))}
            </div>
          )}

          <button
            className="btn-primary"
            onClick={handleApproveVideos}
          >
            ✅ Approva Video
          </button>
        </div>
      )}

      {/* STEP 7: VIDEO COMBINATION */}
      {state.current_step === 'videos_approved' && (
        <div className="step-content">
          <h3>🎞️ Step 7: Combinazione Video</h3>
          <p>Video approvati. Procedi alla combinazione finale.</p>
          <button
            className="btn-primary"
            onClick={handleCombineVideos}
            disabled={state.loading}
          >
            🔗 Combina Video
          </button>
        </div>
      )}

      {state.current_step === 'combining_videos' && (
        <div className="step-content">
          <h3>⏳ Combinazione in Corso...</h3>
          <div className="loader"></div>
          <p>FFmpeg sta combinando i video in un unico file...</p>
          <button
            className="btn-secondary"
            onClick={handleCancelWorkflow}
            style={{ marginTop: '20px' }}
          >
            ❌ Cancella Workflow
          </button>
        </div>
      )}

      {/* COMPLETED */}
      {state.current_step === 'completed' && (
        <div className="step-content" style={{ textAlign: 'center', padding: '40px' }}>
          <h2>🎉 Workflow Completato!</h2>
          <p>Il video finale è stato generato con successo.</p>

          {state.outputs.final_video && (
            <div style={{ marginTop: '20px' }}>
              <video
                src={`${API_URL}/${state.outputs.final_video}`}
                controls
                style={{ maxWidth: '100%', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}
              />
              <p style={{ marginTop: '10px' }}>
                <a
                  href={`${API_URL}/${state.outputs.final_video}`}
                  download
                  className="btn-primary"
                  style={{ display: 'inline-block', textDecoration: 'none' }}
                >
                  📥 Scarica Video
                </a>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProgressBar({ progress }) {
  if (!progress) return null;

  const { current = 0, total = 100, eta_seconds = 0, percentage = 0 } = progress;

  // Format ETA
  const formatETA = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
  };

  return (
    <div style={{ width: '100%', marginTop: '15px' }}>
      <div style={{
        width: '100%',
        height: '24px',
        background: '#e0e0e0',
        borderRadius: '12px',
        overflow: 'hidden',
        position: 'relative'
      }}>
        <div style={{
          width: `${percentage}%`,
          height: '100%',
          background: 'linear-gradient(90deg, #2196f3, #21cbf3)',
          transition: 'width 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <span style={{
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)'
          }}>
            {percentage}%
          </span>
        </div>
      </div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '8px',
        fontSize: '13px',
        color: '#666'
      }}>
        <span>Tentativo {current}/{total}</span>
        <span>ETA: {formatETA(eta_seconds)}</span>
      </div>
    </div>
  );
}

function StepBadge({ label, active, completed }) {
  return (
    <div
      style={{
        padding: '8px 16px',
        borderRadius: '20px',
        background: completed ? '#4caf50' : active ? '#2196f3' : '#e0e0e0',
        color: completed || active ? 'white' : '#666',
        fontSize: '14px',
        fontWeight: 'bold'
      }}
    >
      {completed && '✓ '}{label}
    </div>
  );
}

export default App;
